import { PLAY_EVENT, ErrorCode, H5_EXT_EVENT, Plugin, H5Player } from '@tencent/thumbplayer-h5';
import { __awaiter, __generator, __assign, __extends } from 'tslib';
import { netTool, domTool, requireModule } from '@tencent/thumbplayer-core';

/* eslint-disable @typescript-eslint/naming-convention */
var WaterMarkCoverClassName = 'thumbplayer-watermark-cover';
var WatermarkPluginName = 'ui-watermark';
var MaxRequestRetry = 3;
var RequestTimeout = 5000;
var LottieSDKUrl = 'https://vm.gtimg.cn/thumbplayer/lottie/5.5.6/lottie.min.js';
var WatermarkError;
(function (WatermarkError) {
    WatermarkError["WATERMARK_REQUEST_FAILED"] = "001";
})(WatermarkError || (WatermarkError = {}));
var WatermarkEvents;
(function (WatermarkEvents) {
    WatermarkEvents["READY"] = "ready";
})(WatermarkEvents || (WatermarkEvents = {}));
var ActionWatermarkType;
(function (ActionWatermarkType) {
    ActionWatermarkType["IMAGE"] = "image";
    ActionWatermarkType["LOTTIE"] = "lottie";
})(ActionWatermarkType || (ActionWatermarkType = {}));
var ReferenceType;
(function (ReferenceType) {
    ReferenceType[ReferenceType["TOP_RIGHT"] = 0] = "TOP_RIGHT";
    ReferenceType[ReferenceType["TOP_LEFT"] = 1] = "TOP_LEFT";
    ReferenceType[ReferenceType["BOTTOM_LEFT"] = 2] = "BOTTOM_LEFT";
    ReferenceType[ReferenceType["BOTTOM_RIGHT"] = 3] = "BOTTOM_RIGHT";
})(ReferenceType || (ReferenceType = {}));

function requestActionWatermark(url, duration) {
    return __awaiter(this, void 0, void 0, function () {
        var err, i, res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!url)
                        return [2 /*return*/, Promise.reject(err)];
                    i = 0;
                    _a.label = 1;
                case 1:
                    if (!(i < MaxRequestRetry)) return [3 /*break*/, 6];
                    _a.label = 2;
                case 2:
                    _a.trys.push([2, 4, , 5]);
                    return [4 /*yield*/, netTool.jsonp(url, {
                            timeout: RequestTimeout,
                        })];
                case 3:
                    res = _a.sent();
                    return [2 /*return*/, parseActionWatermark(res, duration)];
                case 4:
                    _a.sent();
                    err = WatermarkError.WATERMARK_REQUEST_FAILED;
                    return [3 /*break*/, 5];
                case 5:
                    i++;
                    return [3 /*break*/, 1];
                case 6: return [2 /*return*/, Promise.reject(err)];
            }
        });
    });
}
/**
 * 动态水印协议 http://tapd.woa.com/qqvideo_prj/markdown_wikis/show/#1210114481001145149
 *
 * 动态水印按组（下发array，每个item为一组）和场景（scenes字段）划分
 * 1. 每组有一个开始时间，同时有一个重复次数，在视频时长内循环播放
 * 2. 每组内按scene划分，每个scene有相对于当前循环的开始时间和结束时间
 *
 * 此函数用于解析动态水印协议，将group和scene信息分离，以水印id为key，保存该水印相对于视频时长的绝对时间schedule
 */
function parseActionWatermark(res, videoDuration) {
    var schedules = {};
    var watermarks = {};
    var start = res.start, watermarkDuration = res.duration, repeat = res.repeat, rw = res.rw;
    // 假设单次循环大于视频时长，循环则最多执行一次；所以这里取ceil
    var actualRepeatTime = repeat === 0 ? Math.ceil(videoDuration / watermarkDuration) : repeat;
    var _loop_1 = function (i) {
        // 计算当前循环的基准起点，scene.in是按这个时间偏移的
        var baseStartTime = start + i * watermarkDuration;
        res.scenes.forEach(function (scene) {
            var wi = scene.wi, startTime = scene.in, endTime = scene.out, startFrom = scene.start, endAt = scene.end;
            wi.forEach(function (watermark) {
                watermarks[watermark.id] = __assign(__assign({}, watermark), { actionType: ActionWatermarkType.IMAGE, rw: rw });
                // 首次记录
                if (!schedules[watermark.id])
                    schedules[watermark.id] = [];
                // 部分水印仅在特定循环时展示，为0标识都展示。
                if (i >= startFrom && (i <= endAt || i === 0)) {
                    schedules[watermark.id].push({
                        start: (baseStartTime + startTime) / 1000,
                        end: (baseStartTime + endTime) / 1000,
                    });
                }
            });
        });
    };
    for (var i = 0; i < actualRepeatTime; i++) {
        _loop_1(i);
    }
    return {
        schedules: schedules,
        watermarks: watermarks,
    };
}

function computeActualPlaytime(time, adRanges, duration) {
    var actualTime = time;
    adRanges.forEach(function (range) {
        if (actualTime > range.start) {
            actualTime += range.end - range.start;
        }
        else if (actualTime + duration > range.start) {
            actualTime = range.end;
        }
    });
    return actualTime;
}
function computeTopLeftOffset(canvasOrigin, canvasSize, watermarkReferencePoint, watermarkSize, referenceType) {
    var left = canvasOrigin[0] + watermarkReferencePoint[0];
    if (referenceType === ReferenceType.BOTTOM_RIGHT || referenceType === ReferenceType.TOP_RIGHT) {
        left = canvasOrigin[0] + canvasSize[0] - (watermarkReferencePoint[0] + watermarkSize[0]);
    }
    var top = canvasOrigin[1] + watermarkReferencePoint[1];
    if (referenceType === ReferenceType.BOTTOM_RIGHT || referenceType === ReferenceType.BOTTOM_LEFT) {
        top = canvasOrigin[1] + canvasSize[1] - (watermarkReferencePoint[1] + watermarkSize[1]);
    }
    return [left, top];
}
/** 计算水印的css信息 */
function computeCss(watermark, container, videoSize) {
    var x = watermark.x, y = watermark.y, h = watermark.h, w = watermark.w, a = watermark.a, _a = watermark.rw, rw = _a === void 0 ? 0 : _a, _b = watermark.position, position = _b === void 0 ? ReferenceType.TOP_RIGHT : _b;
    var vWidth = videoSize.width, vHeight = videoSize.height;
    if (vWidth <= 0)
        vWidth = container.clientWidth;
    if (vHeight <= 0)
        vHeight = container.clientHeight;
    var base = rw > 0 ? (Math.min(vHeight, vWidth) / rw) : 1;
    var wmX = x * base;
    var wmY = y * base;
    var wmH = h * base;
    var wmW = w * base;
    // 视频相对于容器的缩放比例
    var ratio = Math.min(container.clientWidth / vWidth, container.clientHeight / vHeight);
    // 视频渲染出来的实际大小
    var renderVideoWidth = vWidth * ratio;
    var renderVideoHeight = vHeight * ratio;
    // 视频坐标系的原点（左上角）
    var renderOriginX = Math.max((container.clientWidth - renderVideoWidth) / 2, 0);
    var renderOriginY = Math.max((container.clientHeight - renderVideoHeight) / 2, 0);
    // 水印的渲染大小
    var renderWmWidth = wmW * ratio;
    var renderWmHeight = wmH * ratio;
    // 水印的xy定位
    var _c = computeTopLeftOffset([renderOriginX, renderOriginY], [renderVideoWidth, renderVideoHeight], [wmX * ratio, wmY * ratio], [renderWmWidth, renderWmHeight], position), renderX = _c[0], renderY = _c[1];
    // 测试边界
    var playerDim = { x: renderOriginX, y: renderOriginY, w: renderVideoWidth, h: renderVideoHeight };
    var wmDim = { x: renderX, y: renderY, w: renderWmWidth, h: renderWmHeight };
    var css = {
        left: renderX + "px",
        top: renderY + "px",
        width: renderWmWidth + "px",
        height: renderWmHeight + "px",
        position: 'absolute',
        display: 'block',
        opacity: "" + a / 100,
        userSelect: 'none',
    };
    if (!isInScreen(playerDim, wmDim)) {
        css.display = 'none';
    }
    return css;
}
/**
   * 判断水印的css是否在画面内
   * @param container 容器的坐标信息
   * @param item 水印的坐标信息
   */
function isInScreen(container, item) {
    var ox = container.x, oy = container.y, ow = container.w, oh = container.h;
    var x = item.x, y = item.y, w = item.w, h = item.h;
    // 左边界左侧
    if (x < ox)
        return false;
    // 上边界上侧
    if (y < oy)
        return false;
    // 右边界右侧
    if (x > ox + ow - w)
        return false;
    // 下边界下侧
    return y <= oy + oh - h;
}

/** 根据水印时长、当前播放时间、预期展示时间换算出当前水印的偏移时间 */
function offsetAnimationTime(time, schedule, duration) {
    for (var _i = 0, schedule_1 = schedule; _i < schedule_1.length; _i++) {
        var _a = schedule_1[_i], start = _a.start, end = _a.end;
        if (time < start || time > end)
            continue;
        return (((time - start) * 1000) % duration);
    }
    return time;
}
function parseAnimationWatermark(res, adRanges) {
    if (adRanges === void 0) { adRanges = []; }
    var rw = res.rw, scenes = res.scenes;
    var schedules = {};
    var watermarks = {};
    scenes.forEach(function (scene) {
        var wi = scene.wi, stime = scene.stime;
        wi.forEach(function (watermark, index) {
            // 这里注意一下单位，广告点位信息都是秒级时间戳
            var adjustedStime = stime.map((function (i) { return computeActualPlaytime(i / 1000, adRanges, watermark.duration / 1000); }));
            var id = "animation-id-" + index;
            watermarks[id] = __assign(__assign({}, watermark), { id: id, url: watermark.web_surl, actionType: ActionWatermarkType.LOTTIE, rw: rw });
            schedules[id] = adjustedStime.map(function (i) { return ({
                start: i,
                end: (i + watermark.duration),
            }); });
        });
    });
    return {
        schedules: schedules,
        watermarks: watermarks,
    };
}
function loadLottie() {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (window.lottie)
                        return [2 /*return*/];
                    return [4 /*yield*/, domTool.loadScript(LottieSDKUrl)];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    });
}
function requestAnimationWatermark(url, adRanges) {
    return __awaiter(this, void 0, void 0, function () {
        var err, i, res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!url)
                        return [2 /*return*/, Promise.reject(err)];
                    i = 0;
                    _a.label = 1;
                case 1:
                    if (!(i < MaxRequestRetry)) return [3 /*break*/, 6];
                    _a.label = 2;
                case 2:
                    _a.trys.push([2, 4, , 5]);
                    return [4 /*yield*/, netTool.jsonp(url, {
                            timeout: RequestTimeout,
                        })];
                case 3:
                    res = _a.sent();
                    return [2 /*return*/, parseAnimationWatermark(res, adRanges)];
                case 4:
                    _a.sent();
                    err = WatermarkError.WATERMARK_REQUEST_FAILED;
                    return [3 /*break*/, 5];
                case 5:
                    i++;
                    return [3 /*break*/, 1];
                case 6: return [2 /*return*/, Promise.reject(err)];
            }
        });
    });
}

var AnimationWatermark = /** @class */ (function () {
    function AnimationWatermark(container, config, showOnInit) {
        if (showOnInit === void 0) { showOnInit = false; }
        this.lottieAnimation = null;
        this.shown = false;
        var cfg = config.cfg, schedule = config.schedule, duration = config.duration;
        this.id = cfg.id;
        this.config = config;
        this.container = container;
        this.schedule = schedule;
        this.duration = duration;
        this.initLottie(cfg.url);
        if (showOnInit) {
            this.show();
        }
        else {
            this.hide();
        }
    }
    AnimationWatermark.prototype.destroy = function () {
        var _a, _b, _c;
        (_a = this.lottieAnimation) === null || _a === void 0 ? void 0 : _a.destroy();
        if ((_b = this.element) === null || _b === void 0 ? void 0 : _b.parentNode) {
            (_c = this.element) === null || _c === void 0 ? void 0 : _c.remove();
        }
        this.element = null;
        this.lottieAnimation = null;
        this.schedule = null;
        this.config = null;
        this.container = null;
    };
    AnimationWatermark.prototype.restore = function () {
        this.initLottie(this.config.cfg.url);
    };
    AnimationWatermark.prototype.play = function (time) {
        if (!this.shown || !this.lottieAnimation)
            return;
        if (typeof time === 'undefined') {
            this.lottieAnimation.play();
            return;
        }
        var timeInMS = offsetAnimationTime(time, this.schedule, this.duration);
        this.lottieAnimation.goToAndPlay(timeInMS);
    };
    AnimationWatermark.prototype.pause = function () {
        if (!this.shown || !this.lottieAnimation)
            return;
        this.lottieAnimation.pause();
    };
    AnimationWatermark.prototype.draw = function (css) {
        var _this = this;
        if (!this.element)
            return;
        Object.keys(css).forEach(function (attr) {
            var _a;
            (_a = _this.element) === null || _a === void 0 ? void 0 : _a.style.setProperty(attr, css[attr]);
        });
    };
    AnimationWatermark.prototype.show = function () {
        var _a;
        (_a = this.element) === null || _a === void 0 ? void 0 : _a.style.setProperty('display', 'block');
        this.shown = true;
    };
    AnimationWatermark.prototype.hide = function () {
        var _a;
        if (!this.shown || !this.lottieAnimation)
            return;
        (_a = this.element) === null || _a === void 0 ? void 0 : _a.style.setProperty('display', 'none');
        this.lottieAnimation.stop();
        this.shown = false;
    };
    AnimationWatermark.prototype.initLottie = function (url) {
        return __awaiter(this, void 0, void 0, function () {
            var lottie;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.createWatermarkNode();
                        return [4 /*yield*/, loadLottie()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, requireModule('lottie', 'lottie')];
                    case 2:
                        lottie = _a.sent();
                        this.lottieAnimation = lottie === null || lottie === void 0 ? void 0 : lottie.loadAnimation({
                            container: this.element,
                            loop: true,
                            autoplay: false,
                            path: url,
                        });
                        return [2 /*return*/];
                }
            });
        });
    };
    AnimationWatermark.prototype.createWatermarkNode = function () {
        var _a = this, config = _a.config, container = _a.container;
        var css = config.css;
        var div = document.createElement('div');
        Object.keys(css).forEach(function (attr) {
            div.style.setProperty(attr, css[attr]);
        });
        this.element = div;
        container.appendChild(div);
    };
    return AnimationWatermark;
}());

var CoverWatermark = /** @class */ (function () {
    function CoverWatermark(container, config) {
        this.container = container;
        this.config = config;
        this.id = config.cfg.id;
        this.createWatermarkNode();
    }
    CoverWatermark.prototype.draw = function (css) { };
    CoverWatermark.prototype.restore = function () {
        this.createWatermarkNode();
    };
    CoverWatermark.prototype.show = function () {
        var _a;
        (_a = this.element) === null || _a === void 0 ? void 0 : _a.style.setProperty('display', 'block');
    };
    CoverWatermark.prototype.hide = function () { };
    CoverWatermark.prototype.play = function () { };
    CoverWatermark.prototype.pause = function () { };
    CoverWatermark.prototype.destroy = function () {
        var _a;
        if ((_a = this.element) === null || _a === void 0 ? void 0 : _a.parentNode)
            this.element.remove();
        this.element = null;
        this.container = null;
        this.config = null;
    };
    CoverWatermark.prototype.createWatermarkNode = function () {
        var _a = this, config = _a.config, container = _a.container;
        var allWaterMarkCover = container.querySelectorAll("." + WaterMarkCoverClassName);
        allWaterMarkCover.forEach(function (ele) { return ele.remove(); });
        var watermarkEl = document.createElement('div');
        var watermarkSVGEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        var watermarkTextEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        var _b = config.svgConfig, svgStyle = _b === void 0 ? {} : _b, _c = config.textConfig, textStyle = _c === void 0 ? {} : _c, watermarkText = config.watermarkText;
        // 设置容器属性
        watermarkEl.classList.add(WaterMarkCoverClassName);
        var watermarkContainerStyle = __assign({ position: 'absolute', width: '100%', height: '100%', userSelect: 'none', zIndex: '1' }, config.css);
        Object.keys(watermarkContainerStyle).forEach(function (key) {
            watermarkEl.style.setProperty(key, watermarkContainerStyle[key]);
        });
        // 设置svg属性
        watermarkSVGEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        var svgConfig = __assign({ width: '200px', height: '200px' }, svgStyle);
        Object.keys(svgConfig).forEach(function (key) {
            watermarkSVGEl.setAttribute(key, svgConfig[key]);
        });
        // 设置字体属性
        var textConfig = __assign({ stroke: 'black', transform: 'translate(20, 120) rotate(-30)', fill: 'rgb(222, 222, 222)', opacity: 0.3, 'stroke-opacity': 0.3, 'stroke-width': 1, 'font-size': '22' }, textStyle);
        Object.keys(textConfig).forEach(function (key) {
            watermarkTextEl.setAttribute(key, textConfig[key]);
        });
        watermarkTextEl.textContent = watermarkText;
        // 组装
        watermarkSVGEl.appendChild(watermarkTextEl);
        // 设置背景url
        watermarkEl.style.backgroundImage = "url(\"data:image/svg+xml;utf8," + watermarkSVGEl.outerHTML.replace(/"/g, '\'') + "\")";
        container.appendChild(watermarkEl);
        this.element = watermarkEl;
        this.config = config;
        return watermarkEl;
    };
    return CoverWatermark;
}());

var ImageWatermark = /** @class */ (function () {
    function ImageWatermark(container, config, showOnInit) {
        if (showOnInit === void 0) { showOnInit = false; }
        this.id = config.cfg.id;
        this.config = config;
        this.container = container;
        this.createWatermarkNode();
        if (showOnInit) {
            this.show();
        }
        else {
            this.hide();
        }
    }
    ImageWatermark.prototype.play = function () { };
    ImageWatermark.prototype.pause = function () { };
    ImageWatermark.prototype.destroy = function () {
        var _a;
        if ((_a = this.element) === null || _a === void 0 ? void 0 : _a.parentNode)
            this.element.remove();
        this.element = null;
        this.config = null;
        this.container = null;
    };
    ImageWatermark.prototype.restore = function () {
        this.createWatermarkNode();
    };
    ImageWatermark.prototype.show = function () {
        this.element.style.setProperty('display', 'block');
    };
    ImageWatermark.prototype.hide = function () {
        var _a;
        (_a = this.element) === null || _a === void 0 ? void 0 : _a.style.setProperty('display', 'none');
    };
    ImageWatermark.prototype.draw = function (css) {
        var _this = this;
        if (this.element) {
            Object.keys(css).forEach(function (attr) {
                _this.element.style.setProperty(attr, css[attr]);
            });
        }
    };
    /**
     * 创建一个新的img节点
     * @param css img节点的css信息
     */
    ImageWatermark.prototype.createWatermarkNode = function () {
        var _a = this, config = _a.config, container = _a.container;
        var css = config.css, cfg = config.cfg;
        var img = new Image();
        Object.keys(css).forEach(function (attr) {
            img.style.setProperty(attr, css[attr]);
        });
        this.element = img;
        this.element.onload = cfg.onload;
        this.element.onerror = cfg.onerror;
        this.element.src = cfg.url;
        this.element.dataset.id = "" + cfg.id;
        container.appendChild(this.element);
        return img;
    };
    return ImageWatermark;
}());

/**
 * 水印监视器组件
 *
 * @export
 * @class WatermarkObserver
 */
var WatermarkObserver = /** @class */ (function () {
    function WatermarkObserver(container) {
        /** 监听的水印列表 */
        this.watchList = {};
        this.container = container;
        this.initMutationObserver();
    }
    WatermarkObserver.prototype.destroy = function () {
        var _a;
        (_a = this.mutationObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
        this.mutationObserver = null;
        this.watchList = {};
    };
    /**
     * 监听指定的水印
     * @param id 水印id
     * @param watermark 水印实例
     */
    WatermarkObserver.prototype.watch = function (id, watermark) {
        this.watchList[id] = watermark;
    };
    /**
     * 解除监听
     *
     * @public
     * @param {(string | number)} id
     */
    WatermarkObserver.prototype.unwatch = function (id) {
        delete this.watchList[id];
    };
    /**
     * 清除所有的水印监听
     *
     * @public
     */
    WatermarkObserver.prototype.clear = function () {
        this.watchList = {};
    };
    WatermarkObserver.prototype.initMutationObserver = function () {
        var _this = this;
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
        this.mutationObserver = new MutationObserver(function () {
            _this.checkIfWatermarkRemoved();
        });
        this.mutationObserver.observe(this.container, { childList: true, subtree: true, attributes: true });
    };
    WatermarkObserver.prototype.checkIfWatermarkRemoved = function () {
        var _this = this;
        // 如果播放器容器已经不存在了，就不再检查了
        if (!this.container.offsetParent)
            return;
        Object.keys(this.watchList).forEach(function (id) {
            var _a;
            var watermark = _this.watchList[id];
            // 以水印元素是否存在offsetParent来判断是否被移除
            if (!((_a = watermark.element) === null || _a === void 0 ? void 0 : _a.offsetParent)) {
                watermark.restore();
                watermark.show();
            }
        });
    };
    return WatermarkObserver;
}());

/**
 * 水印调度器，根据时间段来判断是否显示水印
 *
 * @export
 * @class Scheduler
 */
var Scheduler = /** @class */ (function () {
    function Scheduler(onStatusChange) {
        this.schedules = {};
        this.innerCurrentTime = 0;
        this.onStatusChange = onStatusChange;
    }
    Object.defineProperty(Scheduler.prototype, "currentTime", {
        set: function (t) {
            this.innerCurrentTime = t;
            this.updateWatermarkStatus();
        },
        enumerable: false,
        configurable: true
    });
    /**
     * 新增一个水印的时间表
     *
     * @public
     * @param {(string | number)} id 水印id
     * @param {ISimpleTimeRange[]} schedules 时间表
     */
    Scheduler.prototype.add = function (id, schedules) {
        var _a;
        var _b;
        var _c;
        (_b = (_c = this.schedules)[id]) !== null && _b !== void 0 ? _b : (_c[id] = []);
        (_a = this.schedules[id]).push.apply(_a, schedules);
    };
    /**
     * 移除一个水印的时间表
     *
     * @public
     * @param {(string | number)} id
     */
    Scheduler.prototype.remove = function (id) {
        if (!this.schedules[id])
            return;
        delete this.schedules[id];
        // 移除时间表的同时进入隐藏状态
        this.onStatusChange(id, false);
    };
    /**
     * 获取一个水印的时间表
     *
     * @public
     * @param {(string | number)} id 水印id
     * @returns {ISimpleTimeRange[]} 时间表数组
     */
    Scheduler.prototype.get = function (id) {
        return this.schedules[id];
    };
    /**
     * 移除所有水印的时间表
     *
     * @public
     */
    Scheduler.prototype.clear = function () {
        this.schedules = {};
    };
    /**
     * 销毁组件
     *
     * @public
     */
    Scheduler.prototype.destroy = function () {
        this.clear();
        this.onStatusChange = null;
    };
    /**
     * 根据当前时间更新水印状态
     *
     * @private
     */
    Scheduler.prototype.updateWatermarkStatus = function () {
        var _this = this;
        Object.keys(this.schedules).forEach(function (key) {
            var schedules = _this.schedules[key];
            var willShow = schedules.some(function (_a) {
                var start = _a.start, _b = _a.end, end = _b === void 0 ? Infinity : _b;
                return (_this.innerCurrentTime > start && _this.innerCurrentTime < end);
            });
            _this.onStatusChange(key, willShow);
        });
    };
    return Scheduler;
}());

var str = "<txpdiv data-role=\"txp-ui-watermark-mod\" class=\"txp-watermark\">\n</txpdiv>";

/**
 * Thumbplayer-h5 水印插件，支持动态/静态水印协议
 *
 * 水印协议文档 http://tapd.oa.com/qqvideo_prj/markdown_wikis/show/#1210114481001145149
 */
var WatermarkPlugin = /** @class */ (function (_super) {
    __extends(WatermarkPlugin, _super);
    function WatermarkPlugin(player, config, extra) {
        var _this = _super.call(this, player, config, extra) || this;
        /** 目前渲染的水印，存储的数据第一项为水印实例，第二项为生成水印的原始参数，用于重绘 */
        _this.watermarks = {};
        _this.watermarkLoadingDefer = null;
        /** 监视器，用于防止水印被删除 */
        _this.observer = null;
        /**
         * 播放器resize的回调
         */
        _this.onResize = function () {
            Object.values(_this.watermarks).forEach(function (item) {
                var watermarkNode = item[0], watermarkCfg = item[1];
                if (watermarkCfg) {
                    var newCss = computeCss(watermarkCfg, _this.player.container, _this.player.videoSize);
                    watermarkNode.draw(newCss);
                }
            });
        };
        _this.onWatermarkStatusChange = function (id, watermarkWillShow) {
            if (!_this.watermarks[id])
                return;
            var watermark = _this.watermarks[id][0];
            if (watermarkWillShow) {
                watermark.show();
                watermark.play();
            }
            else {
                watermark.hide();
                watermark.pause();
            }
        };
        _this.config = __assign(__assign({ 
            // 默认开启防盗水印
            enableProtectionWatermark: true, 
            // 默认不开启observer
            enableObserver: false }, (_this.player.config.watermarkConfig || {})), config);
        _this.$watermarkContainer = _this.prepareDom();
        window.addEventListener('resize', _this.onResize);
        player.on(PLAY_EVENT.FULLSCREEN_CHANGE, _this.onResize, -1);
        var defaultText = _this.config.defaultText;
        if (defaultText) {
            _this.watermarkThis(defaultText);
        }
        _this.setupPlayerEvents();
        _this.listenFakeFullscreen();
        _this.scheduler = new Scheduler(_this.onWatermarkStatusChange.bind(_this));
        if (_this.config.enableObserver) {
            _this.observer = new WatermarkObserver(_this.player.container);
        }
        return _this;
    }
    /**
     * 添加一个新的水印
     * @param watermarkConfig 水印的参数，包括大小，相对位置，以及图片url
     * @param showOnInit 是否直接显示
     * @param watch 是否需要observer保护
     */
    WatermarkPlugin.prototype.add = function (watermarkConfig, showOnInit, watch) {
        var _a;
        if (showOnInit === void 0) { showOnInit = false; }
        if (watch === void 0) { watch = false; }
        var id = watermarkConfig.id;
        if (this.watermarks[id]) {
            // console.warn('相同id的水印已经存在，请检查水印的配置');
            return;
        }
        var watermarkInstance = this.watermarkFactory(watermarkConfig, showOnInit);
        this.watermarks[id] = [watermarkInstance, watermarkConfig];
        if (watch) {
            (_a = this.observer) === null || _a === void 0 ? void 0 : _a.watch(id, watermarkInstance);
        }
        return watermarkInstance;
    };
    WatermarkPlugin.prototype.watch = function (id) {
        var _a;
        var watermark = this.watermarks[id][0];
        if (watermark) {
            (_a = this.observer) === null || _a === void 0 ? void 0 : _a.watch(id, watermark);
        }
    };
    WatermarkPlugin.prototype.unwatch = function (id) {
        var _a;
        (_a = this.observer) === null || _a === void 0 ? void 0 : _a.unwatch(id);
    };
    /**
     * 为容器添加一个水印遮罩
     * @param text 水印文字
     * @param styleConfig svg样式配置
     */
    WatermarkPlugin.prototype.watermarkThis = function (text, styleConfig) {
        var _a, _b;
        if (styleConfig === void 0) { styleConfig = {}; }
        (_a = this.observer) === null || _a === void 0 ? void 0 : _a.unwatch(WaterMarkCoverClassName);
        var config = {
            watermarkText: text,
            cfg: {
                id: "cover-" + text,
            },
            svgConfig: styleConfig.svg || {},
            textConfig: styleConfig.text || {},
            css: {},
        };
        var watermark = new CoverWatermark(this.player.container, config);
        this.watermarks[WaterMarkCoverClassName] = [watermark, null];
        (_b = this.observer) === null || _b === void 0 ? void 0 : _b.watch(WaterMarkCoverClassName, watermark);
    };
    /**
     * 移除指定id的水印, 同时移除对应的时间表
     * @param id 需要移除的水印的id
     */
    WatermarkPlugin.prototype.remove = function (id) {
        var _a, _b;
        var target = ((_a = this.watermarks[id]) !== null && _a !== void 0 ? _a : [])[0];
        if (target) {
            (_b = this.observer) === null || _b === void 0 ? void 0 : _b.unwatch(id);
            target.destroy();
            delete this.watermarks[id];
            this.scheduler.remove(id);
        }
    };
    /**
     * 销毁所有的水印，清空缓存
     */
    WatermarkPlugin.prototype.clear = function () {
        var _a;
        Object.values(this.watermarks).forEach(function (_a) {
            var i = _a[0];
            return i.destroy();
        });
        this.watermarks = {};
        (_a = this.observer) === null || _a === void 0 ? void 0 : _a.clear();
        this.scheduler.clear();
    };
    /**
     * 获取指定id的水印实例
     * @param id 水印的id
     */
    WatermarkPlugin.prototype.get = function (id) {
        return this.watermarks[id];
    };
    /**
     * 设定指定id的水印的显示时间, 水印不存在时跳过
     * @param id 水印的id
     * @param start 开始显示的时间，单位秒
     * @param end 结束显示的时间，单位秒
     */
    WatermarkPlugin.prototype.schedule = function (id, start, end) {
        var _a;
        if (end === void 0) { end = Infinity; }
        this.scheduler.add(id, [{ start: start, end: end }]);
        // 如果对某一个水印进行了时间表的设定，那么就不再监听其dom变化
        (_a = this.observer) === null || _a === void 0 ? void 0 : _a.unwatch(id);
    };
    /**
     * 注销指定id的水印的时间表
     * @param id 需要清空时间表的水印的id
     */
    WatermarkPlugin.prototype.unschedule = function (id) {
        this.scheduler.remove(id);
    };
    /** 销毁插件，移除dom元素 */
    WatermarkPlugin.prototype.destroy = function () {
        var _a, _b, _c;
        window.removeEventListener('resize', this.onResize);
        this.player.off(PLAY_EVENT.FULLSCREEN_CHANGE, this.onResize);
        (_a = this.observer) === null || _a === void 0 ? void 0 : _a.destroy();
        this.observer = null;
        this.scheduler.destroy();
        this.scheduler = null;
        if ((_b = this.$watermarkContainer) === null || _b === void 0 ? void 0 : _b.parentNode)
            (_c = this.$watermarkContainer) === null || _c === void 0 ? void 0 : _c.remove();
        _super.prototype.destroy.call(this);
    };
    WatermarkPlugin.prototype.hookPlaySessionStart = function () {
        this.clear();
    };
    WatermarkPlugin.prototype.hookVideoSetlevelEnd = function (param) {
        var _this = this;
        if (param.code !== ErrorCode.SUC)
            return;
        this.clear();
        if (this.watermarkLoadingDefer) {
            this.watermarkLoadingDefer.then(function (res) {
                _this.addPendingData.apply(_this, res);
            });
        }
    };
    WatermarkPlugin.prototype.hookVideoPlaying = function (param) {
        var _this = this;
        if (this.watermarkLoadingDefer && param.isFirst) {
            this.watermarkLoadingDefer.then(function (res) {
                _this.addPendingData.apply(_this, res);
            });
        }
        Object.keys(this.watermarks).forEach(function (id) {
            var wm = _this.watermarks[id][0];
            wm === null || wm === void 0 ? void 0 : wm.play();
        });
    };
    WatermarkPlugin.prototype.hookVideoPause = function (param) {
        var _this = this;
        Object.keys(this.watermarks).forEach(function (id) {
            var wm = _this.watermarks[id][0];
            wm === null || wm === void 0 ? void 0 : wm.pause();
        });
    };
    WatermarkPlugin.prototype.hookVideoSeeked = function () {
        var _this = this;
        Object.keys(this.watermarks).forEach(function (id) {
            var wm = _this.watermarks[id][0];
            wm === null || wm === void 0 ? void 0 : wm.play(_this.player.playtime);
        });
    };
    WatermarkPlugin.prototype.hookVideoTimeupdate = function (param) {
        this.scheduler.currentTime = param.playtime;
    };
    WatermarkPlugin.prototype.watermarkFactory = function (watermark, showOnInit) {
        if (showOnInit === void 0) { showOnInit = false; }
        var _a = watermark.actionType, actionType = _a === void 0 ? ActionWatermarkType.IMAGE : _a;
        switch (actionType) {
            case ActionWatermarkType.IMAGE:
                return this.createImageWatermark(watermark, showOnInit);
            case ActionWatermarkType.LOTTIE:
                return this.createAnimationWatermark(watermark, showOnInit);
        }
    };
    WatermarkPlugin.prototype.createAnimationWatermark = function (watermark, showOnInit) {
        if (showOnInit === void 0) { showOnInit = false; }
        var url = watermark.url, id = watermark.id;
        var css = computeCss(watermark, this.player.container, this.player.videoSize);
        var cfg = {
            css: css,
            cfg: {
                id: id,
                url: url,
            },
            schedule: this.scheduler.get(id),
            duration: watermark.duration,
        };
        var lottieWatermark = new AnimationWatermark(this.$watermarkContainer, cfg, showOnInit);
        return lottieWatermark;
    };
    WatermarkPlugin.prototype.createImageWatermark = function (watermark, showOnInit) {
        if (showOnInit === void 0) { showOnInit = false; }
        var url = watermark.url, surl = watermark.surl, id = watermark.id;
        var css = computeCss(watermark, this.player.container, this.player.videoSize);
        var cfg = {
            css: css,
            cfg: {
                id: id,
                // 优先使用https地址
                url: surl || url,
                onerror: function () {
                    this.remove();
                },
            },
        };
        var imageWatermark = new ImageWatermark(this.$watermarkContainer, cfg, showOnInit);
        return imageWatermark;
    };
    WatermarkPlugin.prototype.setupPlayerEvents = function () {
        var _this = this;
        this.msg.on(H5_EXT_EVENT.GETINFO_OVER, function (_a) {
            var data = _a.data;
            _this.watermarkLoadingDefer = new Promise(function (resolve) { return __awaiter(_this, void 0, void 0, function () {
                var parseData, watermarkList, supplementaryInfo, _a, duration, contentInfo, protectionWatermark, watermarks, timeTable, res_1, res, animationWatermarks_1, schedules;
                var _b, _c, _d;
                return __generator(this, function (_e) {
                    switch (_e.label) {
                        case 0:
                            parseData = data.parseData;
                            if (!parseData)
                                return [2 /*return*/];
                            watermarkList = parseData.watermarkList, supplementaryInfo = parseData.supplementaryInfo, _a = parseData.duration, duration = _a === void 0 ? 0 : _a;
                            contentInfo = (supplementaryInfo || {}).contentInfo;
                            protectionWatermark = (contentInfo || {}).protectionWatermark;
                            if (this.config.enableProtectionWatermark && protectionWatermark) {
                                this.watermarkThis(protectionWatermark);
                            }
                            watermarks = [];
                            timeTable = {};
                            _e.label = 1;
                        case 1:
                            _e.trys.push([1, 3, , 4]);
                            return [4 /*yield*/, requestActionWatermark((_b = watermarkList) === null || _b === void 0 ? void 0 : _b.action, duration * 1000)];
                        case 2:
                            res_1 = _e.sent();
                            watermarks = Object.keys(res_1.watermarks).map(function (key) { return res_1.watermarks[key]; });
                            timeTable = res_1.schedules;
                            return [3 /*break*/, 4];
                        case 3:
                            _e.sent();
                            return [3 /*break*/, 4];
                        case 4:
                            // 如果没有没有动态水印，使用静态水印
                            if (!watermarks.length && (watermarkList === null || watermarkList === void 0 ? void 0 : watermarkList.wi)) {
                                watermarks = watermarkList.wi;
                            }
                            _e.label = 5;
                        case 5:
                            _e.trys.push([5, 7, , 8]);
                            return [4 /*yield*/, requestAnimationWatermark((_c = watermarkList) === null || _c === void 0 ? void 0 : _c.animation, (_d = contentInfo === null || contentInfo === void 0 ? void 0 : contentInfo.ad) !== null && _d !== void 0 ? _d : [])];
                        case 6:
                            res = _e.sent();
                            animationWatermarks_1 = res.watermarks, schedules = res.schedules;
                            watermarks.push.apply(watermarks, Object.keys(animationWatermarks_1).map(function (key) { return animationWatermarks_1[key]; }));
                            timeTable = __assign(__assign({}, timeTable), schedules);
                            return [3 /*break*/, 8];
                        case 7:
                            _e.sent();
                            return [3 /*break*/, 8];
                        case 8:
                            resolve([watermarks, timeTable]);
                            return [2 /*return*/];
                    }
                });
            }); });
        });
    };
    WatermarkPlugin.prototype.addPendingData = function (watermarks, timeTable) {
        var _this = this;
        if (timeTable) {
            Object.keys(timeTable).forEach(function (key) { return _this.scheduler.add(key, timeTable[key]); });
        }
        if (watermarks) {
            watermarks.forEach(function (watermark) {
                var actionType = watermark.actionType;
                // 动态水印时，初始化不展示，也不进行监听
                _this.add(watermark, !actionType, !actionType);
            });
        }
        this.watermarkLoadingDefer = null;
    };
    /**
     * 注入水印的dom容器
     */
    WatermarkPlugin.prototype.prepareDom = function () {
        var container = this.player.container;
        container.insertAdjacentHTML('beforeend', str);
        return container.querySelector('[data-role="txp-ui-watermark-mod"]');
    };
    WatermarkPlugin.prototype.listenFakeFullscreen = function () {
        var _this = this;
        var ctrlName = 'ctrlbar';
        this.msg.on("pluginsetup:" + ctrlName, function () {
            var ctrl = _this.player.plugins[ctrlName];
            ctrl.on('browserFullscreenChange', _this.onResize);
        });
    };
    /** 插件名 */
    WatermarkPlugin.pluginName = WatermarkPluginName;
    return WatermarkPlugin;
}(Plugin));

H5Player.registerPlugin(WatermarkPlugin.pluginName, WatermarkPlugin);

export { WatermarkPlugin };
